import { ClassifierData } from 'types/classifiers';
import {
    GroupEmployeeRoleType,
    GroupMemberRoleType,
    PartyGroup,
    PartyGroupDto,
    PartyGroupEmployee,
    PartyGroupForm,
    PartyGroupParty,
} from 'types/groups';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { sortByField } from 'utils/sort';

/** The classifiers {@link groupDtoToForm} reads, and the form's options. */
export const GROUP_FORM_CLASSIFIERS = [
    'GROUP_PARTNERSHIP_TYPE',
    'BUSINESS_AREA_TYPE',
    'SEGMENT_TYPE',
    'SERVICE_LEVEL_TYPE',
];

const getPrimaryContacts = (parties: PartyGroupParty[]) => {
    return parties
        .filter((item) => item.groupMemberRoleTypeCode === GroupMemberRoleType.PRIMARY_CONTACT)
        .map((item) => ({
            id: item.party.id,
            name: item.party.name,
            firstName: item.party.firstName,
            lastName: item.party.lastName,
        }));
};

const getMembers = (parties: PartyGroupParty[]) => {
    return parties
        .filter((item) => item.groupMemberRoleTypeCode === GroupMemberRoleType.MEMBER)
        .map((item) => ({ id: item.party.id, name: item.party.name, partyType: item.party.partyType }));
};

const getEmployees = (employees: PartyGroupEmployee[], role: GroupEmployeeRoleType) => {
    return employees
        .filter((item) => item.groupEmployeeRoleTypeCode === role)
        .map((item) => ({
            id: item.employee.id,
            name: item.employee.name,
            firstName: item.employee.firstName,
            lastName: item.employee.lastName,
        }));
};

/** The edit form's values for a fetched group. */
export const groupDtoToForm = (partyGroup: PartyGroup, classifiers: Record<string, ClassifierData>): PartyGroupForm => {
    const flatBusinessAreas = (partyGroup.businessAreas || []).map((val) => val.businessAreaTypeCode);
    const businessAreas = getOptionsFromClassifier(classifiers['BUSINESS_AREA_TYPE']).filter(
        (val) => typeof val.value === 'string' && flatBusinessAreas.includes(val.value)
    ) as { label: string; value: string }[];
    return {
        groupType: partyGroup.groupTypeCode ?? null,
        groupName: partyGroup.groupName,
        businessAreas,
        members: sortByField(getMembers(partyGroup.parties || []), 'name'),
        primaryContacts: sortByField(getPrimaryContacts(partyGroup.parties || []), 'name'),
        relationshipManagers: sortByField(
            getEmployees(partyGroup.employees || [], GroupEmployeeRoleType.RELATIONSHIP_MANAGER),
            'name'
        ),
        relationshipPartners: sortByField(
            getEmployees(partyGroup.employees || [], GroupEmployeeRoleType.RELATIONSHIP_PARTNER),
            'name'
        ),
        groupPartnershipType:
            getOptionsFromClassifier<string, string>(classifiers['GROUP_PARTNERSHIP_TYPE']).find(
                (val) => val.value === partyGroup?.groupPartnershipTypeCode
            ) ?? null,
        segmentType:
            getOptionsFromClassifier<string, string>(classifiers['SEGMENT_TYPE']).find(
                (val) => val.value === partyGroup?.segmentTypeCode
            ) ?? null,
        serviceLevelType:
            getOptionsFromClassifier<string, string>(classifiers['SERVICE_LEVEL_TYPE']).find(
                (val) => val.value === partyGroup?.serviceLevelTypeCode
            ) ?? null,
        notes: partyGroup.notes ?? '',
    };
};

/** The create and update request for the form's values. */
export const groupFormToRequest = (form: PartyGroupForm): PartyGroupDto => ({
    groupTypeCode: form.groupType,
    groupName: form.groupName,
    groupPartnershipTypeCode: form.groupPartnershipType ? form.groupPartnershipType.value : null,
    segmentTypeCode: form.segmentType ? form.segmentType.value : null,
    serviceLevelTypeCode: form.serviceLevelType ? form.serviceLevelType.value : null,
    businessAreas: form.businessAreas.map((area) => area.value),
    members: form.members,
    primaryContacts: form.primaryContacts,
    relationshipManagers: form.relationshipManagers,
    relationshipPartners: form.relationshipPartners,
    notes: form.notes,
});

/**
 * The form fields each card of the group page edits. The business areas sit with the members, whose change fills
 * them in.
 */
export const GROUP_SECTION_FIELDS = {
    details: ['groupName', 'groupType', 'groupPartnershipType', 'segmentType', 'serviceLevelType'],
    members: ['businessAreas', 'members', 'primaryContacts', 'relationshipPartners', 'relationshipManagers'],
    notes: ['notes'],
} satisfies Record<string, (keyof PartyGroupForm)[]>;

export type GroupSectionId = keyof typeof GROUP_SECTION_FIELDS;

/**
 * Whether the user may save the group, as the server decides: UPDATE.PARTY_GROUPS.ALL, or MY_OWN on a group the
 * user is a relationship partner or manager of.
 */
export const canEditGroup = (
    partyGroup: PartyGroup,
    userId: number | undefined,
    { canUpdateAll, canUpdateMyOwn }: { canUpdateAll: boolean; canUpdateMyOwn: boolean }
): boolean =>
    canUpdateAll ||
    (canUpdateMyOwn &&
        (partyGroup.employees ?? []).some(
            ({ employee, groupEmployeeRoleTypeCode }) =>
                employee.id === userId &&
                (groupEmployeeRoleTypeCode === GroupEmployeeRoleType.RELATIONSHIP_PARTNER ||
                    groupEmployeeRoleTypeCode === GroupEmployeeRoleType.RELATIONSHIP_MANAGER)
        ));
